// ── imports explicites (C4 — générés par esm-codemod --analyze, maintenus à la main) ──
import { useEffect, useState } from "../00-boot.jsx";
import { REQUEST_SILENCE_REASONS } from "../01-taxonomy-cities.jsx";
import { SEED_DEALS, SEED_REVIEWS } from "../04-i18n-dict.jsx";
import { DEAL_LEAN_SELECT, dealFromDB, lanacFlagsFirstBootWait, lanacLeanSocialOn, listeAJour, reviewFromDB } from "../07-trust-moderation.jsx";
import { lanacSupabaseClient } from "../08-supabase-client.jsx";
// ── Tranche 5c · useDealsState : transactions, avis, invites post-visite ────
/** @param {any} me @param {{sellerModal?:any}} [ports] — sellerModal vient de useUiPrefs (appelé avant). */
function useDealsState(me, ports={}){
  const {sellerModal,convos}=ports; // convos (useMessagingState, appelé avant) → port de C1
  // Lot 7 (Phase 3b) : snapshot serveur {annonce → nb d'offres échouées} de l'acheteur, chargé au boot (façon
  // non-lus). Ferme le trou du pré-contrôle client quand la conversation n'est pas encore chargée en mémoire.
  const [offerFailCounts,setOfferFailCounts]=useState({});
  const [reviews,setReviews]=useState(SEED_REVIEWS);
  // V3-T3b · Mes avis (RLS : reviewer ou vendeur). Le trust score reste 100 % serveur
  // (compute_trust_breakdown) — ce tableau ne sert qu'à l'affichage.
  useEffect(()=>{
    const client=lanacSupabaseClient();
    if(!client||!me?.id)return;
    // EGRESS P2c · contrat 2a : c'était la SEULE requête de boot sans aucune borne (select("*"),
    //  pas de limit, `comment` texte libre compris). Or hors SellerModal (qui fait SON fetch à
    //  l'ouverture, limit 100), les consommateurs — gating « 1 avis/deal », rappels, CTA — ne
    //  lisent que des ids et le rating. À ON : projection maigre + limit 300 (aligné sur le
    //  plafond deals, seul périmètre du gating). À OFF : requête d'origine, intacte.
    let b5cancelled=false;
    (async()=>{
    // EGRESS B5 : porte évaluée APRÈS la réponse `app_flags` sur une première visite (miroir
    //  absent) — sinon l'appareil neuf payait la requête d'origine sans borne (80,5 Ko au
    //  banc, `select("*")`). Appareil instruit : résolution immédiate, zéro coût.
    await lanacFlagsFirstBootWait("lanac:leanSocialServer");
    if(b5cancelled) return;
    const _lean=lanacLeanSocialOn();
    const _q=_lean
      ? client.from("reviews").select("id,deal_id,reviewer_id,seller_id,item_id,rating,ts,created_at").or(`reviewer_id.eq.${me.id},seller_id.eq.${me.id}`).order("ts",{ascending:false}).limit(300)
      : client.from("reviews").select("*").or(`reviewer_id.eq.${me.id},seller_id.eq.${me.id}`).order("ts",{ascending:false});
    _q.then(({data,error})=>{
      if(b5cancelled||error||!Array.isArray(data)) return;
      // ⚠️ MERGE, PLUS REMPLACEMENT (02/08, audit) : ce fetch de boot ECRASAIT les avis
      //  charges par le SellerModal (qui fetch les 100 avis d'un vendeur a l'ouverture).
      //  Fenetre d'origine ~200 ms ; B5 la porte a « reponse app_flags » sur une premiere
      //  visite ⇒ une liste ouverte se VIDAIT sous un entete annoncant « N avis ».
      const frais=data.map(reviewFromDB).filter(Boolean);
      setReviews(prev=>{
        const parId=new Map((prev||[]).map(r=>[String(r.id),r]));
        frais.forEach(r=>parId.set(String(r.id),r));
        return [...parId.values()];
      });
    });
    })(); // B5 : fin de l'enveloppe « attendre les drapeaux avant la porte »
    return ()=>{b5cancelled=true;};
  },[me?.id,me?.gen]);
  const [deals,setDeals]=useState(SEED_DEALS);
  // V3-T3b · Phase 4 — load deals (RLS: seller or buyer) when user changes
  // NOTE: no setDeals([]) on early return — SEED_DEALS must stay visible when no user is logged in
  useEffect(()=>{
    const client=lanacSupabaseClient();
    if(!client||!me?.id) return;
    // Borne A5 : RLS = mes deals uniquement ; plafond 300 (5× le total de TOUTE la base actuelle).
    // ⭐ Egress (20/07/2026) : `select("*")` pesait 42 261 o au boot sur le compte le plus
    //  chargé, dont 9 colonnes inutiles — 4 que `dealFromDB` ne mappe même pas, 5 jamais
    //  lues pour une décision. `DEAL_LEAN_SELECT` : −33,0 %, −13 958 o mesurés.
    //  ⭐⭐ Le gain durable n'est pas l'octet, c'est la MONOTONIE : une liste explicite
    //  empêche toute colonne future d'augmenter l'egress toute seule et en silence.
    //  Sûreté d'écriture : `dealToDB` omet les champs null ⇒ jamais d'écrasement en base
    //  (propriété pinnée par tests/deals-lean-projection.test.mjs).
    //  (04/11/2026, B-DEMARRAGE-1) LA LISTE À JOUR (listeAJour, 07-trust-moderation) : seules les lignes CHANGÉES depuis la liste gardée
    //   repartent — les mêmes colonnes, le même ordre (ts ↓, puis id : le départage, ajouté aux deux côtés), le même plafond ; une
    //   réponse douteuse ou une erreur : la lecture d'avant, telle quelle.
    //  (revue d'architecte APRÈS) une réponse d'un chargement DÉPASSÉ (membre changé, me.gen relu) ne pose plus rien : la fenêtre a grandi
    //   (mémoire, empreintes, commande, peut-être la lecture d'avant) — les accords d'un autre ne s'affichent jamais.
    let cancelled=false;
    const poser=data=>{ if(!cancelled) setDeals(data.map(dealFromDB).filter(Boolean)); };
    const lireAvant=()=>client.from("deals").select(DEAL_LEAN_SELECT).order("ts",{ascending:false}).order("id",{ascending:true}).limit(300).then(({data,error})=>{
      if(!error&&Array.isArray(data))poser(data);
    });
    listeAJour({client,rpc:"deals_a_jour",nom:"accords",uid:me.id,octetsMax:250000}).then(rows=>cancelled?null:(rows?poser(rows):lireAvant()));
    return()=>{cancelled=true;};
  },[me?.id,me?.gen]);
  const [dealPrompt,setDealPrompt]=useState(null);
  const [postVisitReserveItem,setPostVisitReserveItem]=useState(null);
  const [postVisitReserveContext,setPostVisitReserveContext]=useState(null);
  const [reviewPromptSeller,setReviewPromptSeller]=useState(null);
  const [pendingGateIntent,setPendingGateIntent]=useState(null);
  // BLOC2-1 · avis d'un vendeur chargés À LA DEMANDE (port : sellerModal).
  useEffect(()=>{
    const sid=sellerModal?.id;
    if(!sid) return;
    const client=lanacSupabaseClient();
    if(!client) return;
    client.from("reviews").select("*").eq("seller_id",sid).order("ts",{ascending:false}).limit(100).then(({data,error})=>{
      if(error||!Array.isArray(data)||!data.length) return;
      setReviews(prev=>{
        const seen=new Set((prev||[]).map(r=>r.id));
        const add=data.map(reviewFromDB).filter(r=>r&&!seen.has(r.id));
        return add.length?[...add,...(prev||[])]:prev;
      });
    });
  },[sellerModal?.id]);
  // ── C1 (17/07/2026) — closedOfferAttemptsForItem remonté de App(), verbatim.
  //  Compte les tentatives d'offre FERMÉES d'un acheteur sur une annonce (plafond
  //  OFFER_MAX_PER_ITEM). Fermeture 0 : n'utilise que `convos` (port) + `offerFailCounts`
  //  (état de CE hook) + me + imports. Retourné. Miroir du trigger serveur is_failed_offer.
  const closedOfferAttemptsForItem=(item,buyerId=me?.id)=>{
    if(!item||!buyerId) return 0;
    const sellerId=item.ownerId;
    // Scan LOCAL (couvre les offres faites/retirées PENDANT la session, même hors snapshot).
    const liveScan=convos
      .filter(c=>String(c.refId)===String(item.id)&&c.participants.includes(buyerId)&&c.participants.includes(sellerId))
      .flatMap(c=>c.messages||[])
      .filter(m=>m.kind==="offer"
        &&((m.data?.buyerId||m.from)===buyerId)&&(m.data?.sellerId||sellerId)===sellerId
        &&(m.status==="declined"||m.status==="cancelled"||(m.status==="counterDeclined"&&!(Number(m.data?.buyerCounterPrice)>0)))
        &&!REQUEST_SILENCE_REASONS.has(m.data?.autoClosedReason||""))
      .length;
    // Snapshot SERVEUR au boot (couvre le cas « conversation pas chargée en mémoire »). max des deux = source
    // unique côté serveur (my_failed_offer_counts partage is_failed_offer avec le trigger) ; jamais sous-estimé.
    const snap=(buyerId===me?.id)?Number(offerFailCounts[String(item.id)]||0):0;
    return Math.max(liveScan,snap);
  };
  return {closedOfferAttemptsForItem,
    offerFailCounts,setOfferFailCounts,reviews,setReviews,deals,setDeals,dealPrompt,setDealPrompt,
    postVisitReserveItem,setPostVisitReserveItem,postVisitReserveContext,setPostVisitReserveContext,
    reviewPromptSeller,setReviewPromptSeller,pendingGateIntent,setPendingGateIntent};
}
export { useDealsState };
